/**
 * Loading, validating and sorting `src/data/notifications.json`.
 *
 * One malformed hand-edited entry must never break the site: invalid entries and
 * duplicate ids are dropped (and logged) rather than thrown, so a typo degrades to
 * "one fewer notification shown", not a crash.
 */

import rawNotifications from '@/data/notifications.json'
import { parseIstDateTime } from './istDate'

export type NotificationType = 'maintenance' | 'update' | 'feature' | 'important' | 'news'
export type NotificationPriority = 'high' | 'normal'

export interface SiteNotification {
  id: string
  title: string
  message: string
  /** "DD Mon YYYY, h:mm AM/PM", interpreted as IST. */
  date: string
  type: NotificationType
  priority: NotificationPriority
  active: boolean
  link: string | null
}

const VALID_TYPES = new Set<string>(['maintenance', 'update', 'feature', 'important', 'news'])
const VALID_PRIORITIES = new Set<string>(['high', 'normal'])

const isValidNotification = (value: unknown): value is SiteNotification => {
  if (!value || typeof value !== 'object') return false
  const n = value as Record<string, unknown>
  return (
    typeof n.id === 'string' &&
    n.id.trim() !== '' &&
    typeof n.title === 'string' &&
    n.title.trim() !== '' &&
    typeof n.message === 'string' &&
    typeof n.date === 'string' &&
    parseIstDateTime(n.date) !== null &&
    typeof n.type === 'string' &&
    VALID_TYPES.has(n.type) &&
    typeof n.priority === 'string' &&
    VALID_PRIORITIES.has(n.priority) &&
    typeof n.active === 'boolean' &&
    (n.link === null || n.link === undefined || typeof n.link === 'string')
  )
}

/** Reads, validates and de-duplicates the raw JSON. Never throws. */
export const loadNotifications = (source: unknown = rawNotifications): SiteNotification[] => {
  if (!Array.isArray(source)) {
    console.error('[notifications] notifications.json is not an array — showing no notifications.')
    return []
  }

  const seenIds = new Set<string>()
  const valid: SiteNotification[] = []
  for (const entry of source) {
    if (!isValidNotification(entry)) {
      console.error('[notifications] dropped a malformed entry:', entry)
      continue
    }
    if (seenIds.has(entry.id)) {
      console.error(`[notifications] duplicate id "${entry.id}" — keeping the first, dropping this one.`)
      continue
    }
    seenIds.add(entry.id)
    valid.push({ ...entry, link: entry.link ?? null })
  }
  return valid
}

const byDateDesc = (a: SiteNotification, b: SiteNotification): number =>
  (parseIstDateTime(b.date) ?? 0) - (parseIstDateTime(a.date) ?? 0)

/**
 * Active notifications, newest first, uncapped.
 *
 * `NotificationBell` (the header icon) shows `.length` as its badge count and slices
 * the first 5 of this for its dropdown; `.length === 0` is also what tells it to render
 * nothing at all.
 */
export const getActiveNotifications = (all: SiteNotification[] = loadNotifications()): SiteNotification[] =>
  all.filter((n) => n.active).sort(byDateDesc)

/** Every notification (active and archived), newest first — the /announcements archive. */
export const getAllAnnouncements = (all: SiteNotification[] = loadNotifications()): SiteNotification[] =>
  [...all].sort(byDateDesc)
